import React from 'react';
import PropTypes from 'prop-types';

/* ══════════════════════════════════════════════════════════════════════════
 * LegalSections — the legal prose renderer (Terms & Conditions / Privacy
 * Policy): sections of `p` / `ul` / `lines` / `h4` blocks, raw URLs and
 * e-mail addresses turned into inline links. Used by the webshop LegalPage
 * and by every checkout's privacy SidePanel (webshop brands, OVER/LL).
 * Styles: `.legal-page__*` in HelpCenter.css.
 *
 *   sections — [{ title?, blocks: [{ type: 'p'|'ul'|'lines'|'h4', text?, items?, lines? }] }]
 * ══════════════════════════════════════════════════════════════════════════ */

const LINK_PATTERN = /(https?:\/\/[^\s,)]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;

/** Turn raw URLs / e-mail addresses inside body copy into inline links. */
export const linkify = (text) =>
  text.split(LINK_PATTERN).map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={i} className="helpcenter__inline-link" href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      );
    }
    if (/^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/.test(part)) {
      return (
        <a key={i} className="helpcenter__inline-link" href={`mailto:${part}`}>
          {part}
        </a>
      );
    }
    return part;
  });

export const LegalBlock = ({ block }) => {
  switch (block.type) {
    case 'h4':
      return <h3 className="legal-page__subheading typo-h4">{block.text}</h3>;
    case 'ul':
      return (
        <ul className="legal-page__list typo-body">
          {block.items.map((item, i) => (
            <li key={i}>{linkify(item)}</li>
          ))}
        </ul>
      );
    case 'lines':
      return (
        <p className="legal-page__text typo-body">
          {block.lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 ? <br /> : null}
              {linkify(line)}
            </React.Fragment>
          ))}
        </p>
      );
    case 'p':
    default:
      return <p className="legal-page__text typo-body">{linkify(block.text)}</p>;
  }
};

const LegalSections = ({ sections = [] }) => (
  <>
    {sections.map((section, i) => (
      <section className="legal-page__section" key={i}>
        {section.title ? (
          <h2 className="helpcenter__section-title typo-h3">{section.title}</h2>
        ) : null}
        {section.blocks.map((block, j) => (
          <LegalBlock key={j} block={block} />
        ))}
      </section>
    ))}
  </>
);

LegalSections.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    blocks: PropTypes.arrayOf(PropTypes.object).isRequired,
  })),
};

export default LegalSections;
